<template>
  <v-card class="screenshot-card" elevation="2" hover @click="$emit('click')">
    <!-- Image Section -->
    <div class="screenshot-image-container">
      <img
        :src="thumbnailUrl"
        class="cursor-pointer screenshot-image"
        @click="$emit('click')"
      />
      <!-- Status Overlay -->
      <div class="status-overlay">
        <v-chip small :color="statusColor" dark>
          {{ screenshot.isOnline ? 'Online' : 'Offline' }}
        </v-chip>
        <v-chip
          v-if="showCaptureStatus"
          small
          :color="captureStatusColor"
          dark
          class="ml-2"
        >
          {{ screenshot.captureStatus || 'Unknown' }}
        </v-chip>
      </div>
    </div>

    <!-- Card Content -->
    <v-card-title class="text-subtitle-1">
      {{ screenshot.displayName || screenshot.deviceName || screenshot.name || screenshot.deviceId }}
    </v-card-title>

    <v-card-text>
      <div class="text-caption">
        <div v-if="screenshot.serial" class="serial-number">
          <strong>Serial:</strong> <span class="font-monospace">{{ screenshot.serial }}</span>
        </div>
        <div v-if="screenshot.facilityName"><strong>Facility:</strong> {{ screenshot.facilityName }}</div>
        <div><strong>Last Captured:</strong> {{ formatDate(screenshot.lastCaptured) }}</div>
        <div><strong>Display ID:</strong> {{ screenshot.deviceId }}</div>
        <div v-if="screenshot.error" class="error--text">
          <strong>Error:</strong> {{ screenshot.error }}
        </div>
      </div>
    </v-card-text>

    <v-card-actions>
      <v-btn
        small
        color="primary"
        :disabled="!screenshot.deviceId"
        @click.stop="clearWebStorageAndReboot"
      >
        Clear Web Storage + Reboot
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import moment from 'moment';
import * as firebase from 'firebase/app';
import 'firebase/functions';
import { getRegionName } from '@/utils/functionsRegion';

export default {
  name: 'ScreenshotCard',
  props: {
    screenshot: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {};
  },
  computed: {
    thumbnailUrl() {
      return this.screenshot.fullImageUrl || this.screenshot.thumbnailUrl || require('@/assets/no-image.jpg');
    },
    showCaptureStatus() {
      return this.screenshot.captureStatus && this.screenshot.captureStatus !== 'success';
    },
    statusColor() {
      return this.screenshot.isOnline ? 'green' : 'grey';
    },
    captureStatusColor() {
      if (this.screenshot.captureStatus === 'success') return 'green';
      if (this.screenshot.captureStatus === 'error') return 'red';
      if (this.screenshot.captureStatus === 'pending') return 'orange';
      return 'grey';
    },
  },
  methods: {
    formatDate(date) {
      if (!date) return 'Never';
      const timestamp = date.seconds ? date.seconds * 1000 : date;
      return moment(timestamp).fromNow();
    },
    async clearWebStorageAndReboot() {
      if (!this.screenshot.deviceId) return;

      const displayName = this.screenshot.displayName || this.screenshot.deviceName || this.screenshot.name || this.screenshot.deviceId;

      // Add to reboot queue immediately
      this.$emit('reboot-queued', {
        deviceId: this.screenshot.deviceId,
        displayName,
      });

      // Show success toast immediately
      this.$toast.open({
        message: `Request sent to clear web storage + reboot for device: ${displayName}`,
        type: 'success',
        duration: 4000,
        dismissible: true,
      });

      try {
        await firebase
          .app()
          .functions(getRegionName())
          .httpsCallable('digitalSignageScreenshots-clearWebStorageAndRebootCallable')({
            identifier: this.screenshot.deviceId,
          });
      } catch (error) {
        console.error('Clear/reboot failed:', {
          code: error?.code,
          message: error?.message,
          details: error?.details,
        });
        this.$toast.open({
          message: error?.details
            ? `${error.message} - ${error.details}`
            : error?.message || 'Unable to clear web storage and reboot.',
          type: 'error',
          duration: 6000,
          dismissible: true,
        });
      }
    },
  },
};
</script>

<style scoped lang="scss">
.screenshot-card {
  cursor: pointer;
  transition: transform 0.2s;
  padding: 0 !important;
  overflow: hidden;

  &:hover {
    transform: translateY(-4px);
  }

  .cursor-pointer {
    cursor: pointer;
  }

  .screenshot-image-container {
    height: 200px;
    width: 100%;
    background-color: #f5f5f5;
    position: relative;
    padding: 0;
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .screenshot-image {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
    display: block;
  }

  .status-overlay {
    position: absolute;
    top: 8px;
    right: 8px;
    display: flex;
    gap: 4px;
    z-index: 1;
  }

  .serial-number {
    .font-monospace {
      font-family: 'Courier New', Courier, monospace;
      font-size: 0.9em;
      color: #666;
    }
  }
}
</style>
